import React from 'react'
import { Card, CardContent, CardTitle, CardDescription } from '../ui/Card'

const formatCurrency = amount => {
  if (!amount && amount !== 0) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatCurrencyWithSign = amount => {
  if (!amount && amount !== 0) return 'N/A'
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  if (amount < 0) {
    return `-${formatter.format(Math.abs(amount))}`
  } else {
    return `+${formatter.format(amount)}`
  }
}

const formatDate = dateString => {
  if (!dateString) return 'N/A'

  const [year, month, day] = dateString.split('-').map(num => parseInt(num))

  // month is 0-indexed in JS Date
  const date = new Date(year, month - 1, day)

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

const formatPayFrequency = frequency => {
  if (!frequency) return 'N/A'

  const frequencyMap = {
    BIWEEKLY: 'Twice a month',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    SEMI_MONTHLY: 'Twice a month',
    UNKNOWN: 'Irregular',
  }

  return frequencyMap[frequency] || frequency
}

const BankAccountItem = ({ account }) => {
  if (!account) return null

  const accountType = account.type
    ? account.type.charAt(0).toUpperCase() + account.type.slice(1)
    : 'Account'

  return (
    <div className='d-flex align-items-center gap-3 mb-4'>
      {account.logo ? (
        <img
          src={`data:image/png;base64,${account.logo}`}
          alt={`${account.name} logo`}
          className='rounded-circle'
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
            backgroundColor: '#f8f9fa',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          className='rounded-circle d-flex align-items-center justify-content-center'
          style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#6c757d',
            color: 'white',
            fontSize: '1.5rem',
            flexShrink: 0,
          }}
        >
          {account.name.charAt(0)}
        </div>
      )}
      <div className='flex-grow-1'>
        <h3 className='mb-0 fw-bold'>{account.name}</h3>
        <div className='text-muted'>
          {account.institution_name} • {accountType} • {account.mask}
        </div>
        <h3 className='fw-bold'>{formatCurrency(account.total_balance)}</h3>
      </div>
    </div>
  )
}

const IncomeSourceItem = ({ source }) => {
  if (!source) return null

  return (
    <div>
      <div className='d-flex align-items-center'>
        <h2 className='mb-0 fw-bold'>{source.employer_name}</h2>
        {source.type && (
          <span className='badge bg-light text-dark rounded-pill px-3 py-1 ms-2'>
            {source.type}
          </span>
        )}
      </div>
      <p className='mb-0'>
        Average monthly income: {formatCurrency(source.average_monthly_income)}
      </p>
      <p className='mb-0'>Frequency: {formatPayFrequency(source.pay_frequency)}</p>
      {source.next_payment_date && (
        <p className='mb-0'>Next payment: {formatDate(source.next_payment_date)}</p>
      )}
    </div>
  )
}

const FinancialMetric = ({ label, value }) => (
  <div className='mb-4'>
    <div className='text-muted mb-1'>{label}</div>
    <h2 className='mb-0'>{value}</h2>
  </div>
)

const FinancialSnapshotCard = ({ financialReport }) => {
  if (!financialReport) {
    return (
      <Card id='financial-snapshot'>
        <CardContent>
          <CardTitle>
            <h2>Budget</h2>
          </CardTitle>
          <CardDescription>
            <p>No financial data available.</p>
          </CardDescription>
        </CardContent>
      </Card>
    )
  }

  const {
    average_monthly_balance,
    average_monthly_income,
    average_monthly_expenses,
    income_last_year,
    bank_accounts,
    income_sources,
  } = financialReport

  return (
    <Card id='financial-snapshot'>
      <CardContent>
        <h2 className='fw-bold mb-4'>Budget</h2>

        <FinancialMetric
          label='Average monthly income'
          value={formatCurrencyWithSign(average_monthly_income)}
        />

        <FinancialMetric
          label='Average monthly spend'
          value={formatCurrencyWithSign(-Math.abs(average_monthly_expenses))}
        />

        <FinancialMetric label='Income last year' value={formatCurrency(income_last_year)} />

        {average_monthly_balance && (
          <FinancialMetric
            label='Average monthly balance'
            value={formatCurrency(average_monthly_balance)}
          />
        )}

        <h2 className='fw-bold mt-5 mb-4'>Bank Accounts</h2>

        {bank_accounts && bank_accounts.length > 0 ? (
          bank_accounts.map((account, index) => <BankAccountItem key={index} account={account} />)
        ) : (
          <p>No bank accounts found.</p>
        )}

        {income_sources && income_sources.length > 0 && (
          <>
            <h2 className='fw-bold mt-5 mb-4'>
              Income
              <div className='fs-6 fw-normal text-muted'>
                {income_sources.length} {income_sources.length === 1 ? 'source' : 'sources'}
              </div>
            </h2>
            <div className='d-flex flex-column gap-4'>
              {income_sources.map((source, index) => (
                <div key={index}>
                  <div className='text-muted fw-bold mb-1'>Income {index + 1}</div>
                  <IncomeSourceItem source={source} />
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default FinancialSnapshotCard
